import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Activity,
  Mail,
  HardDrive,
  Calendar,
  MessageSquare,
  Video,
  FileText,
  Image,
  File,
  ExternalLink,
  RefreshCw,
  Filter,
  Loader2,
  Bell,
  Send,
  Hash,
  Clock,
  CheckCircle,
  Circle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// App configurations with icons and colors
const APP_CONFIG = {
  gmail: {
    name: 'Gmail',
    icon: Mail,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    emoji: '📧'
  },
  google_drive: {
    name: 'Google Drive',
    icon: HardDrive,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    emoji: '📁'
  },
  google_calendar: {
    name: 'Google Calendar',
    icon: Calendar,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    emoji: '📅'
  },
  slack: {
    name: 'Slack',
    icon: Hash,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    emoji: '💬'
  },
  discord: {
    name: 'Discord',
    icon: MessageSquare,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    emoji: '🎮'
  },
  telegram: {
    name: 'Telegram',
    icon: Send,
    color: 'text-sky-500',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    emoji: '✈️'
  }
};

// Get file icon based on mime type
const getFileIcon = (mimeType, fileName) => {
  if (mimeType?.includes('video') || fileName?.match(/\.(mp4|mov|avi|mkv)$/i)) {
    return <Video className="h-4 w-4 text-red-500" />;
  }
  if (mimeType?.includes('image') || fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
    return <Image className="h-4 w-4 text-green-500" />;
  }
  if (mimeType?.includes('pdf') || fileName?.match(/\.pdf$/i)) {
    return <FileText className="h-4 w-4 text-red-600" />;
  }
  if (mimeType?.includes('document') || fileName?.match(/\.(doc|docx)$/i)) {
    return <FileText className="h-4 w-4 text-blue-600" />;
  }
  return <File className="h-4 w-4 text-gray-500" />;
};

const GlobalActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchActivities = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      let url = `${API}/api/app-notifications?limit=100`;
      if (activeFilter !== 'all') {
        url += `&source=${activeFilter}`;
      }
      
      const [activitiesRes, statsRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/activity-feed/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setActivities(activitiesRes.data || []);
      setStats(statsRes.data || { total: 0, unread: 0, by_source: {} });
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchActivities();
    
    // Poll for new activities every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [fetchActivities]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchActivities();
    setRefreshing(false);
    toast.success('Activity feed refreshed');
  };

  const markAsRead = async (activityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/api/app-notifications/${activityId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivities(prev => 
        prev.map(a => a.id === activityId ? { ...a, read: true } : a)
      );
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openInApp = (activity) => {
    if (!activity.read) {
      markAsRead(activity.id);
    }
    if (activity.url) {
      window.open(activity.url, '_blank');
    }
  };

  // Group activities by date
  const groupActivitiesByDate = () => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    activities.forEach(activity => {
      const date = new Date(activity.timestamp);
      let dateKey;
      
      if (date.toDateString() === today.toDateString()) {
        dateKey = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateKey = 'Yesterday';
      } else {
        dateKey = format(date, 'EEEE, MMMM d, yyyy');
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    
    return Object.entries(groups);
  };

  const groupedActivities = groupActivitiesByDate();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="global-activity-feed">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="h-5 w-5 text-violet-500" />
            Activity Feed
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Real-time notifications from all connected apps
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {/* Total */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === 'all' ? 'ring-2 ring-violet-400' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-50">
                <Bell className="h-4 w-4 text-violet-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">All</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unread */}
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-100">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-100">
                <Circle className="h-4 w-4 text-red-500 fill-red-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-red-700">{stats.unread}</p>
                <p className="text-xs text-red-600">Unread</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Per-source stats */}
        {Object.entries(APP_CONFIG).slice(0, 5).map(([key, config]) => {
          const count = stats.by_source?.[key] || 0;
          const Icon = config.icon;
          
          return (
            <Card 
              key={key}
              className={`cursor-pointer transition-all hover:shadow-md ${activeFilter === key ? 'ring-2 ring-violet-400' : ''}`}
              onClick={() => setActiveFilter(activeFilter === key ? 'all' : key)}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{count}</p>
                    <p className="text-xs text-slate-500">{config.name.split(' ')[0]}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Activity List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" />
              Recent Activity
            </span>
            <Badge variant="outline">
              {activities.length} items
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No activities yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Connect your apps to start receiving notifications
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-6">
                {groupedActivities.map(([date, dateActivities]) => (
                  <div key={date}>
                    {/* Date header */}
                    <div className="sticky top-0 bg-white z-10 py-2 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="text-xs font-medium text-slate-400 px-2 bg-white">
                          {date}
                        </span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    </div>
                    
                    {/* Activities */}
                    <div className="space-y-3">
                      {dateActivities.map((activity) => {
                        const config = APP_CONFIG[activity.source] || {
                          name: activity.source,
                          icon: Bell,
                          color: 'text-gray-500',
                          bgColor: 'bg-gray-50',
                          borderColor: 'border-gray-200',
                          emoji: '🔔'
                        };
                        const Icon = config.icon;
                        
                        return (
                          <div
                            key={activity.id}
                            onClick={() => openInApp(activity)}
                            className={`flex gap-3 p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                              activity.read 
                                ? 'bg-white border-slate-200' 
                                : `${config.bgColor} ${config.borderColor}`
                            }`}
                          >
                            {/* App Icon */}
                            <div className={`flex-shrink-0 p-2 rounded-lg bg-white shadow-sm`}>
                              <Icon className={`h-5 w-5 ${config.color}`} />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* App name badge and time */}
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-white">
                                  {config.emoji} {config.name}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                  {format(new Date(activity.timestamp), 'h:mm a')}
                                </span>
                                {!activity.read && (
                                  <Circle className="h-2 w-2 fill-violet-500 text-violet-500" />
                                )}
                              </div>
                              
                              {/* Message */}
                              <p className="text-sm text-slate-700">
                                <span className="font-semibold text-blue-600">
                                  {activity.sender_name}
                                </span>
                                {' '}{activity.action}{' '}
                                {activity.target && (
                                  <span className="text-slate-600">{activity.target}</span>
                                )}
                              </p>
                              
                              {/* Item preview (file, email, etc.) */}
                              {activity.item && activity.item.name && (
                                <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                                  {getFileIcon(activity.item.mimeType, activity.item.name)}
                                  <span className="text-sm text-blue-600 font-medium truncate max-w-[300px]">
                                    {activity.item.name}
                                  </span>
                                  <ExternalLink className="h-3 w-3 text-slate-400" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Webhook Setup Info */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Webhook URLs for Push Notifications
          </h4>
          <div className="grid md:grid-cols-2 gap-2 text-xs">
            <div className="p-2 bg-white rounded border">
              <span className="text-slate-500">Gmail:</span>
              <code className="ml-2 text-violet-600">{window.location.origin}/api/webhooks/gmail</code>
            </div>
            <div className="p-2 bg-white rounded border">
              <span className="text-slate-500">Drive:</span>
              <code className="ml-2 text-violet-600">{window.location.origin}/api/webhooks/google-drive</code>
            </div>
            <div className="p-2 bg-white rounded border">
              <span className="text-slate-500">Slack:</span>
              <code className="ml-2 text-violet-600">{window.location.origin}/api/webhooks/slack</code>
            </div>
            <div className="p-2 bg-white rounded border">
              <span className="text-slate-500">Calendar:</span>
              <code className="ml-2 text-violet-600">{window.location.origin}/api/webhooks/google-calendar</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GlobalActivityFeed;
